import axios from "axios";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { API_URL } from "const/constants";
import { P } from "const/permissions";
import { hasPermission } from "utils/permissionUtils";

const BASE = `${API_URL}/referral-overview`;

const unwrap = (data) => data?.data ?? data;

/**
 * Single badge source for every referral queue screen. One cache key feeds the sidebar, the
 * partners-list topbar and the referred-clients header, so those badges never cost more than one
 * request per freshness window no matter how many of those screens mount.
 */
export const REFERRAL_OVERVIEW_KEY = ["referral-overview"];

export const partnerOverviewKey = (partnerId) => [...REFERRAL_OVERVIEW_KEY, "partner", partnerId];

/** Counts stay fresh for a minute; mutations invalidate explicitly instead of polling. */
export const REFERRAL_OVERVIEW_STALE_TIME = 60_000;

const canSeeReferralQueues = () =>
  [
    P.REFERRAL.PARTNER_READ,
    P.REFERRAL.LEAD_READ,
    P.REFERRAL.APPLICATION_READ,
    P.REFERRAL.MESSAGE_READ,
  ].some((key) => hasPermission(key));

export const fetchReferralOverview = async () => unwrap((await axios.get(`${BASE}/summary`)).data);

export const fetchPartnerOverview = async (partnerId) =>
  unwrap((await axios.get(`${BASE}/partner/${partnerId}`)).data);

const overviewOptions = { staleTime: REFERRAL_OVERVIEW_STALE_TIME, refetchOnWindowFocus: false };

export const useReferralOverview = () => {
  const token = localStorage.getItem("token");
  return useQuery(REFERRAL_OVERVIEW_KEY, fetchReferralOverview, {
    ...overviewOptions,
    // No token (logged out) or no referral screen visible: no badges to feed, so no request.
    enabled: Boolean(token) && canSeeReferralQueues(),
    refetchOnMount: true,
    retry: false,
  });
};

export const usePartnerOverview = (partnerId) => {
  const token = localStorage.getItem("token");
  return useQuery(partnerOverviewKey(partnerId), () => fetchPartnerOverview(partnerId), {
    ...overviewOptions,
    enabled: Boolean(token) && Boolean(partnerId) && canSeeReferralQueues(),
    refetchOnMount: true,
    retry: false,
  });
};

/** Queues changed (ticket moved, request reviewed, lead activated, message read): badges refresh. */
export const useInvalidateReferralOverview = () => {
  const queryClient = useQueryClient();
  return (partnerId) => {
    queryClient.invalidateQueries(REFERRAL_OVERVIEW_KEY);
    if (partnerId) queryClient.invalidateQueries(partnerOverviewKey(partnerId));
  };
};
